/**
 * `mount` + `bootstrapWidget` for embeddables system widgets.
 *
 * `mount` injects the embeddables default-CSS resolver
 * (`/w/<widget>/<widget>.css` + `common.css` + icomoon) and the child-IIFE JS
 * resolver (used by CSR layout widgets to load their slot children). Both live
 * here because they encode the `/w/` + manifest + dev URL rules that
 * widget-core intentionally doesn't know about.
 *
 * `bootstrapWidget` is the single entry every widget IIFE calls: it registers
 * the widget in the cross-bundle registry (so a CSR layout can mount it as a
 * slot child) and auto-mounts it into every `[data-hr-widget-container]`.
 *
 * Plugin widgets bypass this — widget-core's mount has its own default
 * resolver that points at the `/p/` proxy on the assets origin.
 */
import {
  mount as coreMount,
  registerWidget,
  type MountConfig,
} from "@homerunner-next/widget-core/runtime";
import type { WidgetCategory } from "@homerunner-next/widget-core/contracts";
import type { ComponentType } from "react";
import { getDefaultCSSUrls, getDefaultJsUrls } from "./utils";

/**
 * Runtime asset-origin override the host page sets on `window.HRWidgetRuntime`
 * (dashboard preview, plugin standalone preview, white-label dashboards). The
 * embeddables asset helpers bake a build-time origin; this lets a CSR layout's
 * dynamically-loaded child IIFEs/CSS follow the SAME origin the page loaded the
 * runtime + layout from — matching how widget-core resolves plugin assets.
 */
function runtimeAssetOrigin(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const rt = (window as unknown as { HRWidgetRuntime?: { proxyBaseUrl?: string } })
    .HRWidgetRuntime;
  return rt?.proxyBaseUrl || undefined;
}

/**
 * Re-base a build-time asset URL onto the runtime origin override, if set.
 * No-op in production embeds / standalone (no override) — returns input as-is,
 * so nothing changes for the common case. Absolute external URLs (e.g. Leaflet
 * passed via explicit `cssUrls`) never flow through here.
 */
function withRuntimeOrigin(url: string): string {
  const origin = runtimeAssetOrigin();
  if (!origin) return url;
  try {
    const u = new URL(url);
    return `${origin.replace(/\/$/, "")}${u.pathname}${u.search}`;
  } catch {
    return url;
  }
}

export function mount(container: HTMLElement, config: MountConfig): void {
  return coreMount(container, {
    resolveDefaultCssUrls: (keyword: string) =>
      getDefaultCSSUrls(keyword).map(withRuntimeOrigin),
    // Widget JS is the last entry (`[runtime, widget]`); layouts load it to pull
    // in each slot child's IIFE so the child self-registers. Re-based onto the
    // runtime origin so preview/white-label hosts fetch children from the right place.
    resolveChildJsUrl: (keyword: string) => withRuntimeOrigin(getDefaultJsUrls(keyword)[1]),
    ...config,
  });
}

export interface BootstrapWidgetConfig {
  widget: ComponentType<any>;
  widgetType: string;
  /** `"layout"` widgets resolve + mount their slot children on CSR. */
  category?: WidgetCategory;
  /**
   * Explicit CSS override — replaces every other source, including the hashed
   * URLs SSR pages provide (#787). Prefer `extraCssUrls` for add-ons.
   */
  cssUrls?: string[];
  /** Extra stylesheets on top of the resolved base (e.g. Leaflet for map widgets). */
  extraCssUrls?: string[];
}

export function bootstrapWidget({
  widget,
  widgetType,
  category,
  cssUrls,
  extraCssUrls,
}: BootstrapWidgetConfig): void {
  const mountInto = (container: HTMLElement) =>
    mount(container, {
      widget,
      widgetType,
      ...(category ? { category } : {}),
      ...(cssUrls ? { cssUrls } : {}),
      ...(extraCssUrls ? { extraCssUrls } : {}),
    });

  // Registered mount carries this widget's exact config, so a CSR layout can
  // mount it as a slot child faithfully (e.g. keeping Leaflet CSS).
  registerWidget(widgetType, { component: widget, mount: mountInto });

  const doMount = () => {
    document
      .querySelectorAll<HTMLElement>("[data-hr-widget-container]")
      .forEach(mountInto);
  };

  if (typeof document !== "undefined" && document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", doMount);
  } else {
    doMount();
  }
}
